import React, { useState } from 'react';
import { Modal, Card, Row, Col, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faEnvelope, 
  faExternalLinkAlt, 
  faPaperPlane,
  faReply,
  faForward,
  faPaperclip,
  faBrain,
  faCalendarAlt,
  faClock,
  faFileAlt,
  faFilePdf,
  faFileImage,
  faFileArchive,
  faFileCode,
  faFileExcel,
  faFilePowerpoint,
  faFileWord,
  faFile,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { 
  UilMessage
} from "@iconscout/react-unicons";
import { ActivityTimelineData } from 'data/project-management/activityTimelineData';
import classNames from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import axiosInstance from 'helpers/api';
import { formatErrorForReact } from 'helpers/utils';
import './EmailDetailModal.css'; // Import responsive styles for email detail modal

interface EmailDetailModalProps {
  show: boolean;
  onHide: () => void;
  activityData: ActivityTimelineData | null;
  originalActivityItem?: any; // Add original activity data for threadId and other metadata
}

type EmailFormInputs = {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  message: string;
  schedule_date?: string;
  attachments?: FileList;
};

interface EmailRecipient {
  email: string;
  name?: string;
  initials?: string;
}

interface EmailAttachment {
  id?: string;
  filename?: string;
  mimeType?: string;
  size?: number;
  attachmentId?: string;
  emailId?: string;
  // Additional fields for Gmail attachments
  name?: string;
  data?: string;
  // Loading state flag
  isLoading?: boolean;
}

interface EmailContent {
  subject: string;
  from: string;
  to: string;
  body: string;
  cc?: string;
  bcc?: string;
  attachments?: EmailAttachment[];
  // Gmail specific fields
  mail_id?: string;
  thread_id?: string;
}

interface RecipientChipProps {
  recipient: EmailRecipient;
  onRemove: () => void;
  variant?: 'primary' | 'secondary' | 'success';
}

const RecipientChip: React.FC<RecipientChipProps> = ({ recipient, onRemove, variant = 'primary' }) => {
  const getInitials = (email: string, name?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const initials = recipient.initials || getInitials(recipient.email, recipient.name);
  const displayName = recipient.name || recipient.email.split('@')[0];

  return (
    <div className={classNames(
      'd-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill border me-1 mb-1',
      `border-${variant}`,
      'recipient-chip'
    )} style={{ 
      backgroundColor: `var(--bs-${variant}-bg-subtle)`, 
      fontSize: '0.8rem',
      lineHeight: '1.2'
    }}>
      <div 
        className={classNames(
          'd-flex align-items-center justify-content-center rounded-circle text-white fw-bold',
          `bg-${variant}`
        )}
        style={{ width: '18px', height: '18px', fontSize: '0.6rem' }}
      >
        {initials}
      </div>
      <span className="text-truncate" style={{ fontSize: '0.8rem', maxWidth: '100px' }}>
        {displayName}
      </span>
      <Button
        variant="link"
        size="sm"
        className="p-0 text-muted"
        onClick={onRemove}
        style={{ fontSize: '0.7rem', width: '14px', height: '14px' }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </Button>
    </div>
  );
};

interface RecipientInputProps {
  label: string;
  recipients: EmailRecipient[];
  onAddRecipient: (recipient: EmailRecipient) => void;
  onRemoveRecipient: (index: number) => void;
  placeholder?: string;
  variant?: 'primary' | 'secondary' | 'success';
  required?: boolean;
  error?: string;
}

const RecipientInput: React.FC<RecipientInputProps> = ({
  label,
  recipients,
  onAddRecipient,
  onRemoveRecipient,
  placeholder = "Enter email address and press Enter",
  variant = 'primary',
  required = false,
  error
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addRecipient();
    }
  };

  const addRecipient = () => {
    const email = inputValue.trim();
    if (email && email.includes('@')) {
      const recipient: EmailRecipient = { email };
      onAddRecipient(recipient);
      setInputValue('');
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addRecipient();
    }
  };

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center">
        <div className="flex-shrink-0 me-2" style={{ width: '32px' }}>
          <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
            {label}:
          </span>
        </div>
        <div 
          className={classNames(
            'border rounded d-flex flex-wrap align-items-center flex-grow-1 px-2 py-1',
            error ? 'border-danger' : 'border-light'
          )}
          style={{ minHeight: '38px', backgroundColor: '#f8f9fa' }}
        >
          {recipients.map((recipient, index) => (
            <RecipientChip
              key={`${recipient.email}-${index}`}
              recipient={recipient}
              onRemove={() => onRemoveRecipient(index)}
              variant={variant}
            />
          ))}
          <Form.Control
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={recipients.length === 0 ? placeholder : ""}
            className="border-0 bg-transparent flex-grow-1 p-0"
            style={{ 
              minWidth: '150px', 
              boxShadow: 'none',
              fontSize: '0.9rem'
            }}
          />
        </div>
      </div>
      {error && <small className="text-danger ms-5">{formatErrorForReact(error)}</small>}
    </div>
  );
};

const EmailDetailModal = ({ show, onHide, activityData, originalActivityItem }: EmailDetailModalProps) => {
  console.log('EmailDetailModal render:', { show, activityData }); // Debug log
  
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [replyToRecipients, setReplyToRecipients] = useState<EmailRecipient[]>([]);
  const [replyCcRecipients, setReplyCcRecipients] = useState<EmailRecipient[]>([]);
  const [replyBccRecipients, setReplyBccRecipients] = useState<EmailRecipient[]>([]);
  const [showBccReply, setShowBccReply] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<EmailFormInputs>();

  const watchReplyMessage = watch("message", "");
  
  if (!activityData || !activityData.content.email) {
    console.log('EmailDetailModal: No data or email content'); // Debug log
    return null;
  }

  // Get email data from activityData.content
  const { email } = activityData.content as { email: EmailContent };
  
  // Process attachments that might come from fetchEmails
  // This handles both standard and Gmail attachment formats (Gmail uses 'parts' instead of 'attachments')
  const attachments: EmailAttachment[] = activityData.attachments || [];
  console.log('Email attachments:', attachments, email); // Debug log for attachments
  
  // Get the mail_id needed for attachment download URLs (could be in different places depending on source)
  const mailId = email.mail_id || (originalActivityItem && originalActivityItem.mail_id);
  
  // Enhanced email content formatter
  const formatEmailContent = (content: string) => {
    if (!content || content.trim() === '') {
      return '<p class="text-muted">No content available</p>';
    }

    let formattedContent = content;

    // Step 1: Clean up common email artifacts
    formattedContent = formattedContent
      // Remove outlook-style forwarded markers
      .replace(/_{10,}/g, '<hr class="my-3">')
      // Clean up excessive whitespace
      .replace(/\s{3,}/g, ' ')
      // Handle forwarded email markers
      .replace(/📧 Forwarded Email/g, '<div class="forwarded-marker"><strong>📧 Forwarded Email</strong></div>')
      // Handle "From:" lines in forwarded emails
      .replace(/From:\s*([^\n]+)/g, '<div class="email-meta-line"><strong>From:</strong> $1</div>')
      // Handle "Sent:" lines
      .replace(/Sent:\s*([^\n]+)/g, '<div class="email-meta-line"><strong>Sent:</strong> $1</div>')
      // Handle "To:" lines
      .replace(/To:\s*([^\n]+)/g, '<div class="email-meta-line"><strong>To:</strong> $1</div>')
      // Handle "Subject:" lines
      .replace(/Subject:\s*([^\n]+)/g, '<div class="email-meta-line"><strong>Subject:</strong> $1</div>');

    // Step 2: Format phone numbers
    formattedContent = formattedContent
      .replace(/(\+?1?\s*\(?[0-9]{3}\)?\s*-?\s*[0-9]{3}\s*-?\s*[0-9]{4})/g, 
        '<span class="phone-number">$1</span>');

    // Step 3: Format email addresses
    formattedContent = formattedContent
      .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, 
        '<a href="mailto:$1" class="email-link">$1</a>');

    // Step 4: Enhanced URL formatting (including localhost)
    formattedContent = formattedContent
      .replace(
        /(\b(?:localhost:[0-9]+\/[^\s<]*|https?:\/\/[^\s<]+)\b)/g,
        (match) => {
          const isLocalhost = match.startsWith('localhost');
          const href = isLocalhost ? `http://${match}` : match;
          return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="url-link">${match}</a>`;
        }
      );

    // Step 5: Format addresses (basic pattern)
    formattedContent = formattedContent
      .replace(/(\d+\s+[A-Za-z\s]+(?:St|Street|Ave|Avenue|Rd|Road|Dr|Drive|Ln|Lane|Blvd|Boulevard|Way|Pl|Place)[^,\n]*(?:,\s*[A-Za-z\s]+)?(?:,?\s*[A-Z]{2}\s*\d{5})?)/g,
        '<div class="address-line">📍 $1</div>');

    // Step 6: Identify and format signatures
    const signaturePatterns = [
      /Thanks,[\s\S]*?$/i,
      /Best regards,[\s\S]*?$/i,
      /Sincerely,[\s\S]*?$/i,
      /Best,[\s\S]*?$/i,
      /-{2,}[\s\S]*?$/,
      /Please consider the environment[\s\S]*?$/i,
      /This electronic mail message[\s\S]*?$/i
    ];

    let mainContent = formattedContent;
    let signature = '';

    for (const pattern of signaturePatterns) {
      const match = formattedContent.match(pattern);
      if (match) {
        signature = match[0];
        mainContent = formattedContent.replace(pattern, '');
        break;
      }
    }

    // Step 7: Format line breaks
    mainContent = mainContent
      .replace(/\n\n+/g, '</p><p>')
      .replace(/\n/g, '<br>');

    // Step 8: Wrap in paragraphs if not already wrapped
    if (!mainContent.includes('<p>') && !mainContent.includes('<div>')) {
      mainContent = `<p>${mainContent}</p>`;
    }

    // Step 9: Format signature if found
    if (signature) {
      signature = signature
        .replace(/\n/g, '<br>')
        .replace(/Thanks,/i, '<strong>Thanks,</strong>')
        .replace(/Best regards,/i, '<strong>Best regards,</strong>')
        .replace(/Sincerely,/i, '<strong>Sincerely,</strong>')
        .replace(/Best,/i, '<strong>Best,</strong>');
      
      signature = `<div class="email-signature">${signature}</div>`;
    }

    return mainContent + signature;
  };
  
  // Format file size for display
  const formatSize = (bytes: number) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleOpenInMail = () => {
    const subject = encodeURIComponent(email.subject);
    const body = encodeURIComponent(email.body);
    const to = encodeURIComponent(email.to);
    const cc = email.cc ? encodeURIComponent(email.cc) : '';
    
    let mailtoLink = `mailto:${to}?subject=${subject}&body=${body}`;
    if (cc) {
      mailtoLink += `&cc=${cc}`;
    }
    
    window.location.href = mailtoLink;
  };

  const handleReplyClick = () => {
    setShowReplyComposer(true);
    setValue('subject', `Re: ${email.subject}`);
    
    // Initialize reply recipients
    setReplyToRecipients([{ email: email.from }]);
    setReplyCcRecipients([]);
    setReplyBccRecipients([]);
    setShowBccReply(false);
  };

  const recipientsToString = (recipients: EmailRecipient[]) => {
    return recipients.map(r => r.email).join(', ');
  };

  const handleFileAttachment = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setReplyAttachments(prev => [...prev, ...fileArray]);
    }
  };

  const removeAttachment = (index: number) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onSubmitReply: SubmitHandler<EmailFormInputs> = async (data) => {
    try {
      // Validate that we have at least one TO recipient
      if (replyToRecipients.length === 0) {
        toast.error('Please add at least one recipient');
        return;
      }

      const replyData = {
        ...data,
        to: recipientsToString(replyToRecipients),
        cc: replyCcRecipients.length > 0 ? recipientsToString(replyCcRecipients) : undefined,
        bcc: replyBccRecipients.length > 0 ? recipientsToString(replyBccRecipients) : undefined,
        threadId: originalActivityItem?.thread_id,
        messageId: originalActivityItem?.mail_id || activityData?.id,
      };

      console.log('📤 Sending reply from EmailDetailModal:', replyData);
      
      // Make actual API call to send reply
      const response = await axiosInstance.post('emails/reply', replyData);
      
      console.log('✅ Reply sent successfully:', response.data);
      toast.success('Reply sent successfully!');
      
      reset();
      setShowReplyComposer(false);
      
      // Reset reply recipients
      setReplyToRecipients([]);
      setReplyCcRecipients([]);
      setReplyBccRecipients([]);
      setShowBccReply(false);
      setReplyAttachments([]);
      
      onHide(); // Close modal after sending
    } catch (error: any) {
      console.error('❌ Error sending reply from EmailDetailModal:', error.response?.data || error);
      
      // Show specific error message from backend
      const errorMessage = error.response?.data?.message || 'Failed to send reply';
      toast.error(errorMessage);
      
      // Check if it's an authentication issue
      if (error.response?.status === 401 || errorMessage.includes('Email not connected')) {
        toast.error('Please reconnect your email account in Settings');
      }
    }
  };

  const handleGenerateAIDraft = async () => {
    setIsGeneratingDraft(true);
    try {
      const aiReply = `Thank you for your email. I've reviewed your message and will get back to you with the information you requested.\n\nBest regards,\n[Your Name]`;
      
      setValue('message', aiReply);
      toast.success('AI reply generated successfully!');
    } catch (error) {
      toast.error('Failed to generate AI reply');
    } finally {
      setIsGeneratingDraft(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      className="email-detail-modal"
      style={{ zIndex: 1060 }}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center justify-content-center bg-primary rounded-circle" 
               style={{ width: '32px', height: '32px' }}>
            <UilMessage size="18" className="text-white" />
          </div>
          <span className="fw-bold text-dark">Email Details</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="modern-email-interface">
          {/* Reply Composer - Shows at top when replying */}
          {showReplyComposer && (
            <div className="reply-composer-section" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <Card className="reply-composer-card m-4 mb-0 shadow-lg border-0">
                <Card.Header className="bg-transparent border-0 p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 d-flex align-items-center gap-3 text-white">
                      <div className="d-flex align-items-center justify-content-center bg-white bg-opacity-20 rounded-circle" 
                           style={{ width: '40px', height: '40px' }}>
                        <FontAwesomeIcon icon={faReply} size="lg" />
                      </div>
                      <div>
                        <div className="fw-bold">Reply to {email.from}</div>
                        <small className="text-white-50">Compose your response</small>
                      </div>
                    </h5>
                    <Button
                      variant="link"
                      size="lg"
                      className="text-white text-white-50-hover p-0"
                      onClick={() => {
                        setShowReplyComposer(false);
                        reset();
                      }}
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-4 bg-white">
                  <Form onSubmit={handleSubmit(onSubmitReply)}>
                    <div className="condensed-email-form">
                      {/* TO and CC Fields */}
                      <div className="recipient-fields mb-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="flex-grow-1">
                            <RecipientInput
                              label="To"
                              recipients={replyToRecipients}
                              onAddRecipient={(recipient) => setReplyToRecipients([...replyToRecipients, recipient])}
                              onRemoveRecipient={(index) => setReplyToRecipients(replyToRecipients.filter((_, i) => i !== index))}
                              placeholder="Enter recipient email address"
                              variant="primary"
                              required
                              error={errors.to?.message}
                            />
                          </div>
                          {!showBccReply && (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-decoration-none ms-3 p-0 text-primary"
                              onClick={() => setShowBccReply(true)}
                              style={{ fontSize: '0.9rem', fontWeight: '500' }}
                            >
                              + BCC
                            </Button>
                          )}
                        </div>
                        
                        <div className="mb-3">
                          <RecipientInput
                            label="Cc"
                            recipients={replyCcRecipients}
                            onAddRecipient={(recipient) => setReplyCcRecipients([...replyCcRecipients, recipient])}
                            onRemoveRecipient={(index) => setReplyCcRecipients(replyCcRecipients.filter((_, i) => i !== index))}
                            placeholder="Add CC recipients"
                            variant="secondary"
                          />
                        </div>
                        
                        {showBccReply && (
                          <div className="mb-3">
                            <RecipientInput
                              label="Bcc"
                              recipients={replyBccRecipients}
                              onAddRecipient={(recipient) => setReplyBccRecipients([...replyBccRecipients, recipient])}
                              onRemoveRecipient={(index) => setReplyBccRecipients(replyBccRecipients.filter((_, i) => i !== index))}
                              placeholder="Add BCC recipients"
                              variant="success"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Subject Field */}
                      <div className="mb-4">
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0" style={{ minWidth: '80px' }}>
                            <FontAwesomeIcon icon={faEnvelope} className="text-muted me-2" />
                            Subject
                          </span>
                          <Form.Control
                            type="text"
                            placeholder="Email subject"
                            {...register("subject", { required: "Subject is required" })}
                            className="border-start-0 bg-light"
                            style={{ fontSize: '0.95rem' }}
                          />
                        </div>
                        {errors.subject && (
                          <small className="text-danger mt-1">{errors.subject.message}</small>
                        )}
                      </div>
                      
                      <Row>
                        <Col md="12">
                          <div className="mb-4">
                            <label className="form-label fw-semibold text-dark mb-3">
                              <FontAwesomeIcon icon={faPaperPlane} className="me-2 text-primary" />
                              Message
                            </label>
                            <div className="border rounded" style={{ minHeight: '250px' }}>
                              <ReactQuill
                                style={{ height: "200px" }}
                                theme="snow"
                                value={watchReplyMessage}
                                onChange={(value) => setValue("message", value)}
                                placeholder="Write your reply here..."
                                modules={{
                                  toolbar: [
                                    ['bold', 'italic', 'underline'],
                                    ['link'],
                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                    ['clean']
                                  ],
                                }}
                              />
                            </div>
                          </div>
                        </Col>
                        
                        {/* Attachments Section */}
                        <Col md="12">
                          <div className="mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <label className="form-label fw-semibold text-dark mb-0">
                                <FontAwesomeIcon icon={faPaperclip} className="me-2 text-secondary" />
                                Attachments
                              </label>
                              <div>
                                <input
                                  type="file"
                                  multiple
                                  onChange={(e) => handleFileAttachment(e.target.files)}
                                  style={{ display: 'none' }}
                                  id="reply-file-input"
                                />
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => document.getElementById('reply-file-input')?.click()}
                                  className="d-flex align-items-center gap-2 px-3"
                                >
                                  <FontAwesomeIcon icon={faPaperclip} size="sm" />
                                  Browse Files
                                </Button>
                              </div>
                            </div>
                            
                            {replyAttachments.length > 0 && (
                              <div className="attachment-list">
                                {replyAttachments.map((file, index) => (
                                  <div key={index} className="attachment-item d-flex align-items-center justify-content-between p-3 border rounded mb-2 bg-light">
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded" 
                                           style={{ width: '40px', height: '40px' }}>
                                        <FontAwesomeIcon icon={faPaperclip} className="text-primary" />
                                      </div>
                                      <div>
                                        <span className="fw-medium text-dark d-block" style={{ fontSize: '0.9rem' }}>{file.name}</span>
                                        <small className="text-muted">({formatFileSize(file.size)})</small>
                                      </div>
                                    </div>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => removeAttachment(index)}
                                      className="text-danger p-0"
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </Col>
                        
                        <Col md="12">
                          <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                            <Button
                              variant="outline-primary"
                              onClick={handleGenerateAIDraft}
                              disabled={isGeneratingDraft}
                              className="d-flex align-items-center gap-2 px-4"
                            >
                              <FontAwesomeIcon icon={faBrain} />
                              {isGeneratingDraft ? 'Generating...' : 'AI Assist'}
                            </Button>
                            
                            <div className="d-flex gap-3">
                              <Button 
                                variant="outline-secondary" 
                                onClick={() => {
                                  setShowReplyComposer(false);
                                  reset();
                                }}
                                className="px-4"
                              >
                                Cancel
                              </Button>
                              <Button 
                                variant="primary" 
                                onClick={handleSubmit(onSubmitReply)}
                                disabled={!watchReplyMessage.trim()}
                                className="px-4 d-flex align-items-center gap-2"
                                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                              >
                                <FontAwesomeIcon icon={faPaperPlane} />
                                Send Reply
                              </Button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Email Thread Content */}
          <div className="email-thread-content p-4">
            <div className="email-thread-container">
              {/* Enhanced Email Header */}
              <div className="email-header-section mb-4">
                <div className="d-flex align-items-start gap-4">
                  {/* Sender Avatar */}
                  <div className="sender-avatar-container">
                    <div className="sender-avatar d-flex align-items-center justify-content-center bg-primary text-white fw-bold rounded-circle shadow" 
                         style={{ width: '48px', height: '48px', fontSize: '1.1rem' }}>
                      {email.from.charAt(0).toUpperCase()}
                    </div>
                    <div className="avatar-status bg-success rounded-circle position-absolute" 
                         style={{ width: '14px', height: '14px', bottom: '2px', right: '2px', border: '2px solid white' }}></div>
                  </div>
                  
                  {/* Email Metadata */}
                  <div className="email-metadata flex-grow-1">
                    <div className="d-flex align-items-center justify-content-between mb-2 email-header-row">
                      <div className="email-sender-info">
                        <h4 className="sender-name mb-1 fw-bold text-dark" style={{ fontSize: 'clamp(0.95rem, 4vw, 1.1rem)' }}>{email.from}</h4>
                        <div className="email-details text-muted">
                          <div className="d-flex flex-wrap align-items-center gap-1 mb-1" style={{ fontSize: 'clamp(0.75rem, 3vw, 0.85rem)' }}>
                            <span className="fw-medium">To:</span>
                            <span className="text-truncate">{email.to}</span>
                            {email.cc && (
                              <>
                                <span className="mx-2">•</span>
                                <span className="fw-medium">CC:</span>
                                <span className="text-truncate">{email.cc}</span>
                              </>
                            )}
                          </div>
                          <div className="email-metadata-time d-flex flex-wrap align-items-center text-muted" style={{ fontSize: 'clamp(0.75rem, 3vw, 0.85rem)' }}>
                            <div className="d-flex align-items-center me-3">
                              <FontAwesomeIcon icon={faCalendarAlt} size="sm" className="me-1" />
                              <span>{formatDate(activityData?.oppositeContent?.date || '')}</span>
                            </div>
                            <div className="d-flex align-items-center me-3">
                              <FontAwesomeIcon icon={faClock} size="sm" className="me-1" />
                              <span>{activityData?.oppositeContent?.time || ''}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons in Header */}
                      <div className="d-flex gap-2 email-header-actions">
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={handleReplyClick}
                          className="d-flex align-items-center gap-2 px-3"
                          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                        >
                          <FontAwesomeIcon icon={faReply} />
                          Reply
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          className="d-flex align-items-center gap-2 px-3"
                        >
                          <FontAwesomeIcon icon={faForward} />
                          Forward
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subject Section */}
              <div className="email-subject-section mb-3">
                <div className="bg-light rounded p-3 border-start border-primary border-4 subject-container">
                  <h4 className="mb-0 fw-bold text-dark email-subject-title" style={{ 
                    fontSize: 'clamp(1rem, 4vw, 1.2rem)',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden'
                  }}>
                    {email.subject}
                  </h4>
                </div>
              </div>

              {/* Email Body Section */}
              <div className="email-body-section">
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-0">
                    <div 
                      className="email-content-body"
                      style={{ 
                        lineHeight: 1.5,
                        fontSize: '1rem',
                        color: '#2c3e50',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        overflow: 'auto',
                        maxHeight: '300px',
                        wordBreak: 'break-word',
                        padding: '8px 12px'
                      }}
                    >
                      <style>
                        {`
                          .email-content-body a {
                            color: #0066cc !important;
                            text-decoration: underline !important;
                            font-weight: 600 !important;
                            background-color: rgba(0, 102, 204, 0.05) !important;
                            padding: 0 3px !important;
                            border-radius: 2px !important;
                            transition: all 0.2s ease !important;
                          }
                          .email-content-body a:hover {
                            color: #004080 !important;
                            background-color: rgba(0, 102, 204, 0.1) !important;
                            text-decoration: underline !important;
                          }
                          .email-content-body .email-link {
                            display: inline-block !important;
                            background: #e6f7ff !important;
                            color: #0066cc !important;
                            font-weight: bold !important;
                            padding: 3px 8px !important;
                            margin: 2px 0 !important;
                            border-radius: 4px !important;
                            text-decoration: none !important;
                            box-shadow: 0 1px 2px rgba(0,0,0,0.1) !important;
                          }
                          .email-content-body .email-link:hover {
                            background: #bae7ff !important;
                            color: #0050b3 !important;
                          }
                        `}
                      </style>
                      <div 
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(formatEmailContent(email.body || '(No content)'))
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </div>

                            {/* Global skeleton animation */}
              <style>
                {`
                  @keyframes pulse {
                    0% { opacity: 0.6; }
                    50% { opacity: 1; }
                    100% { opacity: 0.6; }
                  }
                  .skeleton-element {
                    background: #f0f0f0;
                    animation: pulse 1.5s infinite;
                    border-radius: 4px;
                  }
                `}
              </style>
              
              {/* Email Attachments Section */}
              {attachments && attachments.length > 0 && (
                <div className="email-attachments-section mb-4 mt-4">
                  <div className="bg-light rounded p-3 border">
                    {/* Skeleton header when attachments are loading */}
                    {attachments.some(att => att.isLoading) ? (
                      <>
                        <h5 className="mb-3 d-flex align-items-center">
                          <div className="skeleton-element me-2" style={{ width: '16px', height: '16px', borderRadius: '4px' }}></div>
                          <div className="skeleton-element" style={{ width: '120px', height: '20px' }}></div>
                        </h5>
                        <div className="attachment-list d-flex flex-wrap gap-3">
                          {/* Show one skeleton attachment item */}
                          <div className="attachment-item">
                            <div className="d-flex align-items-center p-2 border rounded bg-white" style={{ width: '220px' }}>
                              <div className="file-icon d-flex align-items-center justify-content-center p-3 bg-light rounded me-2">
                                <div className="skeleton-element" style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '4px'
                                }}></div>
                              </div>
                              <div className="file-details overflow-hidden flex-grow-1">
                                <div className="file-name text-truncate fw-bold" style={{ fontSize: '0.9rem' }}>
                                  <div className="skeleton-element" style={{
                                    width: '80%',
                                    height: '16px',
                                    animationDelay: '0.2s'
                                  }}></div>
                                </div>
                                <div className="file-size text-muted" style={{ fontSize: '0.8rem' }}>
                                  <div className="skeleton-element" style={{
                                    width: '40%',
                                    height: '12px',
                                    animationDelay: '0.4s'
                                  }}></div>
                                </div>
                              </div>
                              <div className="skeleton-element" style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                animationDelay: '0.6s'
                              }}></div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <h5 className="mb-3 d-flex align-items-center">
                          <FontAwesomeIcon icon={faPaperclip} className="me-2 text-primary" />
                          <span className="fw-bold">Attachments ({attachments.length})</span>
                        </h5>
                        <div className="attachment-list d-flex flex-wrap gap-3">
                          {attachments.map((attachment, index) => {
                            // Skip loading attachments as they're handled by the skeleton UI above
                            if (attachment.isLoading) return null;
                            
                            // For normal attachments
                            let fileIcon = faFile;
                            const mimeType = attachment.mimeType?.toLowerCase() || '';
                            
                            if (mimeType.includes('pdf')) fileIcon = faFilePdf;
                            else if (mimeType.includes('image/')) fileIcon = faFileImage;
                            else if (mimeType.includes('zip') || mimeType.includes('compressed')) fileIcon = faFileArchive;
                            else if (mimeType.includes('html') || mimeType.includes('javascript') || mimeType.includes('json')) fileIcon = faFileCode;
                            else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) fileIcon = faFileExcel;
                            else if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) fileIcon = faFilePowerpoint;
                            else if (mimeType.includes('word') || mimeType.includes('document')) fileIcon = faFileWord;
                            else fileIcon = faFileAlt;
                            
                            // Get filename - handle both standard and Gmail formats
                            const displayFilename = attachment.filename || attachment.name || `attachment-${index + 1}`;
                            const displaySize = attachment.size || 0;
                            
                            return (
                              <div key={attachment.id || attachment.attachmentId || index} className="attachment-item">
                                <div className="d-flex align-items-center p-2 border rounded bg-white" style={{ width: '220px' }}>
                                  <div className="file-icon d-flex align-items-center justify-content-center p-3 bg-light rounded me-2">
                                    <FontAwesomeIcon icon={fileIcon} size="lg" className="text-primary" />
                                  </div>
                                  <div className="file-details overflow-hidden flex-grow-1">
                                    <div className="file-name text-truncate fw-bold" style={{ fontSize: '0.9rem' }}>
                                      {displayFilename}
                                    </div>
                                    <div className="file-size text-muted" style={{ fontSize: '0.8rem' }}>
                                      {formatSize(displaySize)}
                                    </div>
                                  </div>
                                  {attachment.data || attachment.attachmentId ? (
                                    <button 
                                      className="btn btn-sm text-primary border-0 bg-transparent"
                                      title="Download attachment"
                                      onClick={() => {
                                        // If we have a mail_id and attachmentId, we can construct a download URL
                                        // mailId is already defined at the component level
                                        if (mailId && attachment.attachmentId) {
                                          const downloadUrl = `/api/email/attachment/${mailId}/${attachment.attachmentId}`;
                                          window.open(downloadUrl, '_blank');
                                        } else if (attachment.data) {
                                          // If we have the attachment data directly, create a download link
                                          // Handle base64 data if available
                                          if (typeof attachment.data === 'string') {
                                            // Convert base64 to binary using browser-native methods
                                            const binaryString = atob(attachment.data.replace(/-/g, '+').replace(/_/g, '/'));
                                            const len = binaryString.length;
                                            const bytes = new Uint8Array(len);
                                            for (let i = 0; i < len; i++) {
                                              bytes[i] = binaryString.charCodeAt(i);
                                            }
                                            
                                            const blob = new Blob(
                                              [bytes],
                                              { type: attachment.mimeType || 'application/octet-stream' }
                                            );
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = displayFilename;
                                            a.click();
                                            URL.revokeObjectURL(url);
                                          }
                                        }
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faDownload} />
                                    </button>
                                  ) : (
                                    <span className="text-muted" title="Download not available">
                                      <FontAwesomeIcon icon={faDownload} opacity={0.5} />
                                    </span>
                                  )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                  )}
                  </div>
                </div>
              )}

              {/* Email Footer Actions */}
              <div className="email-footer-actions mt-3 pt-3 border-top">
                <div className="d-flex justify-content-end flex-wrap gap-2">
                  <Button 
                    variant="primary" 
                    onClick={handleReplyClick}
                    className="d-flex align-items-center gap-1 px-2 py-1"
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}
                  >
                    <FontAwesomeIcon icon={faReply} size="xs" style={{ fontSize: '0.7rem' }} />
                    <span>Reply</span>
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    className="d-flex align-items-center gap-1 px-2 py-1"
                    style={{ 
                      borderRadius: '6px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500' 
                    }}
                  >
                    <FontAwesomeIcon icon={faForward} size="xs" style={{ fontSize: '0.7rem' }} />
                    Forward
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EmailDetailModal; 